import React from 'react';

import { ActionIcon, createStyles, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { InvoiceAction } from 'types';

import { INVOICE_ACTION_TO_READABLE_TITLE_MAP } from './utils';

const useStyles = createStyles(
  (theme, { color, isTransitional }: { color: string; isTransitional: boolean }) => ({
    card: {
      display: 'grid',
      gridTemplateColumns: '50px auto',
      height: 70,
      gridTemplateRows: 'auto',
      alignItems: 'center',
      gap: 0,
      padding: theme.spacing.md,
      marginLeft: isTransitional ? theme.spacing.xl : 0,
    },
    dot: {
      height: 20,
      width: 20,
      borderRadius: '50%',
      backgroundColor: color || theme.colors.gray[3],
    },
    topRow: {
      display: 'grid',
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateRows: 'auto',
      gap: '.5rem',
      whiteSpace: 'nowrap',
      alignItems: 'center',
    },
    bottomRow: {
      display: 'grid',
      gridTemplateColumns: 'auto min-content',
      gridTemplateRows: 'auto',
      gap: '1.5rem',
      whiteSpace: 'nowrap',
      alignItems: 'center',
    },
  }),
);

const stateTransitionMessage = (invoiceAction: InvoiceAction): string => {
  if (invoiceAction.action === 'NOTES_EDITED') return '';
  if (invoiceAction.oldInvoiceState === invoiceAction.newInvoiceState) {
    return invoiceAction.newInvoiceState;
  }
  return `${invoiceAction.oldInvoiceState || ''} → ${invoiceAction.newInvoiceState}`;
};

type InvoiceActivityCardProps = {
  invoiceAction: InvoiceAction;
};

const InvoiceActivityCard = ({ invoiceAction }: InvoiceActivityCardProps): JSX.Element => {
  const { formatCurrency, dateFormat, dateTimeFormat } = useLocalization();

  const standardInvoiceActionMessage = (): string => {
    return `${formatCurrency(invoiceAction.total / 100)} - Due Date ${dayjs(invoiceAction.dueDate).format(
      dateFormat,
    )} - ${invoiceAction.paymentMethod}`;
  };

  const paymentInvoiceActionMessage = (): string => {
    return `${formatCurrency(invoiceAction.total / 100)} - ${invoiceAction.paymentMethod}`;
  };

  const refundInvoiceActionMessage = (): string => {
    return `${formatCurrency((invoiceAction.refundAmount || 0) / 100)} / ${formatCurrency(
      invoiceAction.total / 100,
    )} - ${invoiceAction.paymentMethod}`;
  };

  const packageDeletedActionMessage = (): string => {
    return `${invoiceAction.packageName || ''} - ${dayjs(invoiceAction.createdAt).format(dateFormat)}`;
  };

  const paymentDateEditedMessage = (): string => {
    const previous = invoiceAction.oldPaymentDate
      ? dayjs(invoiceAction.oldPaymentDate).format(dateFormat)
      : '';
    const next = invoiceAction.newPaymentDate ? dayjs(invoiceAction.newPaymentDate).format(dateFormat) : '';
    const dates = `${previous} → ${next}`;
    // An invoice with no method on record carries the two dates alone. "Unknown" is the stored
    // value for exactly that case, so it reads as no method rather than as a method named Unknown.
    const method = invoiceAction.paymentMethod;
    if (!method || method === 'Unknown') {
      return dates;
    }
    return `${dates} - ${method}`;
  };

  const INVOICE_ACTION_MAP: Record<
    InvoiceAction['action'],
    { color: string; title: string; messageFunction: (invoiceAction: InvoiceAction) => string }
  > = {
    CREATED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.CREATED,
      messageFunction: standardInvoiceActionMessage,
    },
    EDITED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.EDITED,
      messageFunction: standardInvoiceActionMessage,
    },
    NOTES_EDITED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.NOTES_EDITED,
      messageFunction: () => 'Notes were added or edited',
    },
    PAYMENT_DATE_EDITED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.PAYMENT_DATE_EDITED,
      messageFunction: paymentDateEditedMessage,
    },
    PAYMENT_INITIATED: {
      color: '#15B8FF',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.PAYMENT_INITIATED,
      messageFunction: paymentInvoiceActionMessage,
    },
    PAYMENT_SUCCEEDED: {
      color: '#3CB553',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.PAYMENT_SUCCEEDED,
      messageFunction: paymentInvoiceActionMessage,
    },
    PAYMENT_FAILED: {
      color: '#EA4E4E',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.PAYMENT_FAILED,
      messageFunction: paymentInvoiceActionMessage,
    },
    BECAME_PAST_DUE: {
      color: '#EA4E4E',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.BECAME_PAST_DUE,
      messageFunction: standardInvoiceActionMessage,
    },
    VOIDED: {
      color: '#FAD100',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.VOIDED,
      messageFunction: () => 'Invoice voided',
    },
    REOPENDED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.REOPENDED,
      messageFunction: standardInvoiceActionMessage,
    },
    REFUND_INITIATED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.REFUND_INITIATED,
      messageFunction: refundInvoiceActionMessage,
    },
    REFUND_SUCCEEDED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.REFUND_SUCCEEDED,
      messageFunction: refundInvoiceActionMessage,
    },
    REFUND_FAILED: {
      color: '#878E95',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.REFUND_FAILED,
      messageFunction: refundInvoiceActionMessage,
    },
    PACKAGE_DELETED: {
      color: '#FAD100',
      title: INVOICE_ACTION_TO_READABLE_TITLE_MAP.PACKAGE_DELETED,
      messageFunction: packageDeletedActionMessage,
    },
  };

  const { color, title, messageFunction } = INVOICE_ACTION_MAP[invoiceAction.action];
  const message = messageFunction(invoiceAction);

  const { classes } = useStyles({
    color: color,
    isTransitional: false,
  });

  return (
    <Paper bg='gray.2' radius='lg' className={classes.card}>
      <div className={classes.dot} />
      <Stack spacing={4}>
        <div className={classes.topRow}>
          <Text size='sm' weight={500}>
            {title}
          </Text>
          {invoiceAction.failureMessage ? (
            <Tooltip
              label={invoiceAction.failureMessage}
              multiline
              withinPortal
              maw={'500px'}
              events={{ hover: true, focus: false, touch: true }}
            >
              <ActionIcon color='red' size='sm'>
                <IconExclamationCircle />
              </ActionIcon>
            </Tooltip>
          ) : (
            <div />
          )}
          <Text size='sm' weight={500} align='right'>
            {dayjs(invoiceAction.createdAt).format(dateTimeFormat)}
          </Text>
        </div>
        <div className={classes.bottomRow}>
          <Text size='sm' color='dimmed' truncate>
            {message}
          </Text>
          <Text size='sm' weight={500} color='dimmed' align='right'>
            {stateTransitionMessage(invoiceAction)}
          </Text>
        </div>
      </Stack>
    </Paper>
  );
};

export default InvoiceActivityCard;
